import React, { useCallback, useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import FiltersWithoutTaxNumber from "./filters/type1/FiltersWithoutTaxNumber";
import FiltersWithTaxNumber from "./filters/type2/FiltersWithTaxNumber";
import IndividualCard from "./IndividualCard";
import IndividualFilters from "./filters/IndividualFilters";
import EmptyClientList from "./list/withoutValues/EmptyClientList";
import CustomFilters from "./filters/type3/CustomFilters";
import { useClientQueryWithErrorHandling, useQueryWithErrorHandling } from "hooks/errorHandling";
import { INIT_PARAM_VALUE_QUERY } from "features/common/queries";
import { get } from "@totalsoft/change-tracking-react";
import {
  GET_PARTNERS_FOR_COMPANY_BY_ZOOT_CONDITIONS_QUERY,
  GET_PARTNERS_FOR_COMPANY_QUERY,
  GET_PARTNERS_FOR_INDIVIDUAL_QUERY,
  GET_PARTNERS_FOR_SOLE_TRADER_QUERY,
} from "../queries";
import ClientList from "./list/withValues/ClientList";
import { LoadingFakeText } from "@totalsoft/rocket-webapp-components-deprecated";
import { useTranslation } from "react-i18next";
import ActionsDialog from "features/common/components/ActionsDialog";
import { Typography, Link, Grid } from "@material-ui/core";
import CreateClient from "./newClient/CreateClient";
import { emptyArray, emptyObject } from "constants/defaultValues";
import { useApolloClient } from "@apollo/client";
import { HELPBUTTON_POPOVER_OPEN_QUERY } from "features/helpButton/queries";
import { set } from "@totalsoft/change-tracking-react";
import useUserData from "hooks/useUserData";
import SectionTitle from "features/common/components/SectionTitle";
import { initialClientForCompanyFilter } from "../constants";
import SoleTraderCard from "./SoleTraderCard";
import SoleTraderFilters from "./filters/SoleTraderFilters";
import moment from "moment";

function ClientContainer(props) {
  const { filterLens, isIndividualLens, partnerListLens, selectedClientLens, validation } = props;
  const { resetFiltersLens, resetSelectedClientLens, isReadOnly, isSoleTraderFeatureLens } = props;

  const { t } = useTranslation();
  const client = useApolloClient();
  const clientQuery = useClientQueryWithErrorHandling();

  const [isWanted, setIsWanted] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [messageDialog, setMessageDialog] = useState();
  const [basicPartnerSearchType, setBasicPartnerSearchType] = useState();
  const { allowIndividualSearch, allowSoleTraderSearch } = useUserData();

  const [loadingList, setLoadingList] = useState(false);

  const isIndividual = isIndividualLens |> get;
  const isSoleTraderFeature = isSoleTraderFeatureLens |> get;
  const partnerList = partnerListLens |> get;

  useEffect(() => {
    setIsWanted(false);
    resetFiltersLens(initialClientForCompanyFilter);
  }, [isIndividual, resetFiltersLens]);

  const { loading } = useQueryWithErrorHandling(INIT_PARAM_VALUE_QUERY, {
    variables: { paramName: "VCP_BasicPartnerSearchViewType" },
    onCompleted: (data) => {
      setBasicPartnerSearchType(data.initializationParamValue.value);
    },
  });

  const afterLoadPartners = useCallback(() => {
    setIsWanted(true);
    setLoadingList(false);
    resetSelectedClientLens(emptyObject); // if search with select partner, then search again with different select
  }, [resetSelectedClientLens]);

  const handleReset = useCallback(() => {
    (partnerListLens |> set)(emptyArray);
    setIsWanted(false);
    resetFiltersLens(initialClientForCompanyFilter);
    resetSelectedClientLens(emptyObject);
  }, [resetFiltersLens, resetSelectedClientLens, partnerListLens]);

  const handleSearch = useCallback(
    async (isExtended) => {
      const filter = filterLens |> get;
      if (isIndividual) {
        setLoadingList(true);
        const { data } = await clientQuery(GET_PARTNERS_FOR_INDIVIDUAL_QUERY, {
          variables: { pin: filter.pin },
          fetchPolicy: "network-only",
        });

        (partnerListLens |> set)(data.getPartnersForIndividual);
        if (data.getPartnersForIndividual.length > 0) {
          resetSelectedClientLens({
            partnerId: data.getPartnersForIndividual[0].partnerId,
            name: t("Client.Individual"),
          });
        }
        setIsWanted(true);
        setLoadingList(false);
      } else {
        if (basicPartnerSearchType != -3) {
          if (isExtended && filter.zip.length >= 40) {
            setMessageDialog(t("Client.ZipCodeTooLong"));
            setOpenDialog(true);
            return;
          } else {
            const validWithoutTaxNumber =
              basicPartnerSearchType != -2 &&
              filter.companyName?.length > 0 &&
              filter.city?.length > 0 &&
              filter.state?.length > 0;
            const validWithTaxNumber =
              basicPartnerSearchType == -2 &&
              (filter.taxId?.length > 0 || (filter.cityDistrictCounty?.length > 0 && filter.companyName?.length > 0));

            if (!validWithTaxNumber && !validWithoutTaxNumber) {
              setMessageDialog(
                basicPartnerSearchType == -2
                  ? t("Client.EnterCompanyNameCityStateWithTaxNumber")
                  : t("Client.EnterCompanyNameCityState")
              );
              setOpenDialog(true);
              return;
            }

            const validLengthWithoutTaxNumber =
              validWithoutTaxNumber &&
              filter.companyName?.length > 3 &&
              filter.city?.length > 1 &&
              filter.state?.length > 1;
            const validLengthWithTaxNumber =
              validWithTaxNumber &&
              (filter.taxId?.length > 3 || (filter.companyName?.length > 3 && filter.cityDistrictCounty?.length > 1));

            if (!validLengthWithTaxNumber && !validLengthWithoutTaxNumber) {
              setMessageDialog(
                basicPartnerSearchType == -2
                  ? t("Client.ForEffectiveSearchWithTaxNumber")
                  : t("Client.ForEffectiveSearch")
              );
              setOpenDialog(true);
              return;
            }
          }
        }
        setLoadingList(true);
        const { data } = await clientQuery(GET_PARTNERS_FOR_COMPANY_QUERY, {
          variables: {
            filters: {
              companyName: filter.companyName,
              legalFormId: filter.legalFormId,
              taxId: filter.taxId,
              street1: filter.street1,
              street2: filter.street2,
              city: filter.city,
              zip: filter.zip,
              state: filter.state,
              cityDistrictCounty: filter.cityDistrictCounty,
              registrationNumber: filter.registrationNumber,
            },
          },
          fetchPolicy: "network-only",
        });

        (partnerListLens |> set)(data.getPartnersForCompany);
        afterLoadPartners();
      }
    },
    [
      t,
      filterLens,
      isIndividual,
      basicPartnerSearchType,
      partnerListLens,
      clientQuery,
      setOpenDialog,
      afterLoadPartners,
      resetSelectedClientLens,
    ]
  );

  const handleSearchByZootConditions = useCallback(
    async (paramValues) => {
      setLoadingList(true);
      const { data } = await clientQuery(GET_PARTNERS_FOR_COMPANY_BY_ZOOT_CONDITIONS_QUERY, {
        variables: { paramValues },
        fetchPolicy: "network-only",
      });

      if (!data?.getPartnersForCompanyByZootConditions) {
        setLoadingList(false);
        return;
      }

      (partnerListLens |> set)(data.getPartnersForCompanyByZootConditions);
      afterLoadPartners();
    },
    [clientQuery, afterLoadPartners, partnerListLens]
  );

  const handleSearchSoleTraders = useCallback(async () => {
    setLoadingList(true);
    const filters = filterLens |> get;
    const { data } = await clientQuery(GET_PARTNERS_FOR_SOLE_TRADER_QUERY, {
      variables: {
        firstName: filters.firstName,
        lastName: filters.lastName,
        birthDate: filters?.birthDate != null ? moment(filters.birthDate).format("YYYY-MM-DDT00:00:00") : null,
        email: filters.email,
        pin: filters.pin,
      },
      fetchPolicy: "network-only",
    });

    if (data?.getPartnersForSoleTrader) {
      (partnerListLens |> set)(data.getPartnersForSoleTrader);
      if (data.getPartnersForSoleTrader.length > 0)
        resetSelectedClientLens({
          partnerId: data.getPartnersForSoleTrader[0].partnerId,
          name: filters.firstName + " " + filters.lastName,
        });
      setIsWanted(true);
    }
    setLoadingList(false);
  }, [clientQuery, partnerListLens, resetSelectedClientLens, filterLens]);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
  }, [setOpenDialog]);

  const dialogActions = useMemo(
    () => [
      {
        caption: t("Dialog.OK"),
        handle: () => {
          handleCloseDialog;
          return true;
        },
      },
    ],
    [t, handleCloseDialog]
  );

  const handleCreateClient = useCallback(() => {
    (selectedClientLens |> set)({ ...(filterLens |> get), createClient: true });
  }, [selectedClientLens, filterLens]);

  const handleContactDeutsche = useCallback(() => {
    client.writeQuery({
      query: HELPBUTTON_POPOVER_OPEN_QUERY,
      data: {
        helpButton: {
          popoverOpen: true,
        },
      },
    });
  }, [client]);

  if (loading) {
    return <LoadingFakeText lines={10} />;
  }

  if ((selectedClientLens |> get)?.createClient) {
    return (
      <>
        <SectionTitle title={t("Client.EnterClientInformation")} />
        <CreateClient
          selectedClientLens={selectedClientLens}
          basicPartnerSearchType={basicPartnerSearchType}
          isCompany={!isIndividual}
          isSoleTraderFeature={isSoleTraderFeature}
          validation={validation}
          onBack={handleReset}
        />
      </>
    );
  }

  return (
    <Grid container spacing={1}>
      {!isReadOnly && (
        <>
          {allowIndividualSearch && !allowSoleTraderSearch && (
            <Grid item xs={12} style={{ marginTop: "10px" }}>
              <IndividualCard isIndividualLens={isIndividualLens} />
            </Grid>
          )}
          {allowSoleTraderSearch && (
            <Grid item xs={12} style={{ marginTop: "10px" }}>
              <SoleTraderCard isSoleTraderFeatureLens={isSoleTraderFeatureLens} onReset={handleReset} />
            </Grid>
          )}
          <Grid item xs={12}>
            <SectionTitle
              title={t("Client.EnterClientInformation")}
              subtitle={t("Client.SearchAndSelectClientFromDatabase")}
            />
          </Grid>

          {isIndividual ? (
            <Grid item xs={12}>
              <IndividualFilters
                filterLens={filterLens}
                onReset={handleReset}
                onSearch={handleSearch}
                loading={loadingList}
              />
            </Grid>
          ) : isSoleTraderFeature ? (
            <Grid item xs={12}>
              <SoleTraderFilters
                filterLens={filterLens}
                onReset={handleReset}
                onSearch={handleSearchSoleTraders}
                loading={loadingList}
              />
            </Grid>
          ) : (
            !loading && (
              <Grid item xs={12}>
                {basicPartnerSearchType == -1 && (
                  <FiltersWithoutTaxNumber
                    filterLens={filterLens}
                    onReset={handleReset}
                    onSearch={handleSearch}
                    loading={loadingList}
                  />
                )}
                {basicPartnerSearchType == -2 && (
                  <FiltersWithTaxNumber
                    filterLens={filterLens}
                    onReset={handleReset}
                    onSearch={handleSearch}
                    loading={loadingList}
                  />
                )}
                {basicPartnerSearchType == -3 && (
                  <CustomFilters
                    filterLens={filterLens}
                    onBasicSearch={handleSearchByZootConditions}
                    onReset={handleReset}
                    onExtendedSearch={handleSearch}
                    loadingList={loadingList}
                  />
                )}
              </Grid>
            )
          )}
          {isWanted && !isIndividual && !isSoleTraderFeature && (
            <Grid item xs={12}>
              <SectionTitle
                title={t("Client.SearchResults", { value: partnerList.length })}
                extraComponent={
                  <>
                    <Typography variant="h5">
                      {t("Client.SearchResult.Text_1")}
                      <Link id="contactDeutsche" underline="always" color="primary" onClick={handleCreateClient}>
                        {t("Client.SearchResult.Text_2")}
                      </Link>
                      {t("Client.SearchResult.Text_3")}
                      <Link id="contactDeutsche" underline="always" color="primary" onClick={handleContactDeutsche}>
                        {t("Client.SearchResult.Text_4")}
                      </Link>
                      {"."}
                    </Typography>
                  </>
                }
              />
            </Grid>
          )}
        </>
      )}
      {partnerList.length > 0 && !isIndividual && !isSoleTraderFeature ? (
        <Grid item xs={12}>
          <ClientList
            partners={partnerList}
            selectedClientLens={selectedClientLens}
            resetSelectedClientLens={resetSelectedClientLens}
            isReadOnly={isReadOnly}
          />
        </Grid>
      ) : (
        <Grid item xs={12}>
          <EmptyClientList
            isCompany={!isIndividual}
            isSoleTraderFeature={isSoleTraderFeature}
            partners={partnerList}
            isWanted={isWanted}
            onCreateClient={handleCreateClient}
            onContactDeutsche={handleContactDeutsche}
          />
        </Grid>
      )}
      <ActionsDialog
        open={openDialog}
        handleClose={handleCloseDialog}
        title={t("Dialog.Warning")}
        message={messageDialog}
        actions={dialogActions}
      />
    </Grid>
  );
}

ClientContainer.propTypes = {
  filterLens: PropTypes.object.isRequired,
  isIndividualLens: PropTypes.object.isRequired,
  partnerListLens: PropTypes.object.isRequired,
  selectedClientLens: PropTypes.object.isRequired,
  resetFiltersLens: PropTypes.func.isRequired,
  resetSelectedClientLens: PropTypes.func.isRequired,
  validation: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  isSoleTraderFeatureLens: PropTypes.object.isRequired,
};

export default ClientContainer;
